import { $applyNodeReplacement, $getRoot, ElementNode, type LexicalNode, type NodeKey, type SerializedElementNode, type Spread } from 'lexical'
import styles from '../EmailEditor.module.scss'
import { type EmailVariable, type TextStyles } from '../emailTemplateTypes'
import { optionalString, requireArray, requireBoolean, requireName, requireObject, requireString } from './serializedKeys'
import { textDeclarations } from './wrapperStyle'

export type SerializedEmailNode = Spread<{ frame: string; variables: EmailVariable[]; textStyles: TextStyles }, SerializedElementNode>

const NODE = 'email'

const readVariable = (value: unknown): EmailVariable => {
  if (typeof value !== 'object' || value === null) {
    throw new Error(`${NODE} needs variables`)
  }
  const entry = value as Record<string, unknown>
  try {
    const fallback = optionalString(entry, NODE, 'fallback')
    const group = optionalString(entry, NODE, 'group')
    return {
      name: requireName(entry, NODE, 'name'),
      label: requireString(entry, NODE, 'label'),
      sample: requireString(entry, NODE, 'sample'),
      insertable: requireBoolean(entry, NODE, 'insertable'),
      ...(fallback === undefined ? {} : { fallback }),
      ...(group === undefined ? {} : { group }),
    }
  } catch {
    throw new Error(`${NODE} needs variables`)
  }
}

const readTextStyles = (json: Record<string, unknown>): TextStyles => {
  const textStyles = requireObject<Record<string, unknown>>(json, NODE, 'textStyles')
  if (!Object.values(textStyles).every((css) => typeof css === 'string')) {
    throw new Error(`${NODE} needs textStyles`)
  }
  return textStyles as TextStyles
}

export class EmailNode extends ElementNode {
  __frame: string
  __variables: EmailVariable[]
  __textStyles: TextStyles

  static getType(): string {
    return NODE
  }

  static clone(node: EmailNode): EmailNode {
    return new EmailNode(node.__frame, node.__variables, node.__textStyles, node.__key)
  }

  constructor(frame: string, variables: EmailVariable[], textStyles: TextStyles, key?: NodeKey) {
    super(key)
    this.__frame = frame
    this.__variables = variables
    this.__textStyles = textStyles
  }

  static importJSON(json: SerializedEmailNode): EmailNode {
    const raw = json as unknown as Record<string, unknown>
    const frame = requireString(raw, NODE, 'frame')
    const variables = requireArray<unknown>(raw, NODE, 'variables').map(readVariable)
    return $createEmailNode(frame, variables, readTextStyles(raw)).updateFromJSON(json)
  }

  exportJSON(): SerializedEmailNode {
    return { ...super.exportJSON(), frame: this.__frame, variables: this.__variables, textStyles: this.__textStyles }
  }

  createDOM(): HTMLElement {
    const dom = document.createElement('div')
    dom.className = styles.doc
    textDeclarations(this.__textStyles.h3 ?? '').forEach(([property, value]) => dom.style.setProperty(`--email-h3-${property}`, value))
    return dom
  }

  updateDOM(): false {
    return false
  }

  getFrame(): string {
    return this.getLatest().__frame
  }

  getVariables(): EmailVariable[] {
    return this.getLatest().__variables
  }

  getTextStyles(): TextStyles {
    return this.getLatest().__textStyles
  }

  canBeEmpty(): false {
    return false
  }

  canIndent(): false {
    return false
  }
}

export const $createEmailNode = (frame: string, variables: EmailVariable[], textStyles: TextStyles): EmailNode =>
  $applyNodeReplacement(new EmailNode(frame, variables, textStyles))

export const $isEmailNode = (node: LexicalNode | null | undefined): node is EmailNode => node instanceof EmailNode

export const $getEmailNode = (): EmailNode | null => {
  const first = $getRoot().getFirstChild()
  return $isEmailNode(first) ? first : null
}
